import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, api, readTableFile } from '../api/client.js'
import { TABLE_ACCEPT } from '../components/ListImport.js'
import type { TaskImportResult, Template } from '../api/types.js'
import { ColumnMapper } from '../components/ColumnMapper.js'
import { PersonPicker } from '../components/people.js'
import { Field, Modal, Notice } from '../components/ui.js'
import { formatDate } from '../lib/format.js'

const STATE_LABEL: Record<TaskImportResult['preview'][number]['state'], string> = {
  ok: 'wird angelegt',
  duplicate: 'schon vorhanden',
  no_object: 'Objekt fehlt',
  no_template: 'Leistung fehlt',
  bad_date: 'Datum unklar',
}

/**
 * The monthly list from the customer, Legionellen, viewings, whatever comes
 * as a sheet, becomes tasks with the customer's ID on each one. The preview
 * shows every row's fate before anything is written, because a list of 200
 * flats with three wrong addresses must not become 197 silent tasks.
 */
export function TaskImportModal({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: (created: number) => void }) {
  const [csv, setCsv] = useState('')
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [templateId, setTemplateId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [result, setResult] = useState<TaskImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<TaskImportResult>('/api/imports/tasks', {
        method: 'POST',
        body: { csv, mapping, templateId: templateId || null, assigneeId: assigneeId || null, dryRun },
      }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
      if (!data.dryRun) onImported(data.created ?? 0)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Import fehlgeschlagen.'),
  })

  function reset() {
    setCsv('')
    setResult(null)
    setError(null)
  }

  return (
    <Modal
      open={open}
      title="Aufgabenliste importieren"
      onClose={() => {
        reset()
        onClose()
      }}
      footer={
        <>
          <button
            type="button"
            className="btn secondary"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Schließen
          </button>
          {!result && (
            <button type="button" className="btn" disabled={!csv || run.isPending} onClick={() => run.mutate(true)}>
              Prüfen
            </button>
          )}
          {result?.dryRun && (
            <button type="button" className="btn" disabled={run.isPending || result.counts.ok === 0} onClick={() => run.mutate(false)}>
              {run.isPending ? 'Wird importiert …' : `${result.counts.ok} Aufgaben anlegen`}
            </button>
          )}
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {result ? (
        <>
          <Notice kind={result.dryRun ? 'info' : 'success'}>
            {result.dryRun ? 'Vorschau, noch nichts angelegt.' : `${result.created} Aufgaben angelegt.`} {result.rows} Zeilen gelesen:{' '}
            {result.counts.ok} in Ordnung
            {result.counts.duplicate > 0 ? `, ${result.counts.duplicate} schon vorhanden` : ''}
            {result.counts.no_object > 0 ? `, ${result.counts.no_object} ohne Objekt` : ''}
            {result.counts.no_template > 0 ? `, ${result.counts.no_template} ohne Leistung` : ''}
            {result.counts.bad_date > 0 ? `, ${result.counts.bad_date} mit unklarem Datum` : ''}.
          </Notice>
          {result.counts.no_object > 0 && (
            <Notice kind="warn">
              Zeilen ohne Objekt: Youtilly-ID oder Adresse ist in Hauswerk unbekannt. Objekt anlegen oder die ID im Objekt nachtragen,
              dann die Datei erneut prüfen.
            </Notice>
          )}
          <div className="table-scroll" style={{ maxHeight: 340, overflowY: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Objekt</th>
                  <th>Leistung</th>
                  <th className="nowrap">Datum</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {result.preview.map((row) => (
                  <tr key={row.line} style={row.state === 'ok' ? undefined : { opacity: 0.75 }}>
                    <td className="nowrap small">{row.line}</td>
                    <td className="small">
                      {row.objectLabel ?? <span className="muted">–</span>}
                      {row.externalRef && <div className="muted">{row.externalRef}</div>}
                    </td>
                    <td className="small">
                      {row.templateName ?? <span className="muted">–</span>}
                      {row.assigneeName && <div className="muted">{row.assigneeName}</div>}
                    </td>
                    <td className="nowrap small">{row.dueDate ? formatDate(row.dueDate) : '–'}</td>
                    <td className="small">
                      <span className={`badge ${row.state === 'ok' ? 'done' : row.state === 'duplicate' ? 'skipped' : 'missed'}`}>{STATE_LABEL[row.state]}</span>
                      <div className="muted">{row.message}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <p className="small" style={{ marginTop: 0 }}>
            Eine Zeile je Aufgabe. Objekte werden über die <strong>Youtilly-ID</strong> gefunden, sonst über <strong>Straße</strong> plus{' '}
            <strong>Wohnung</strong>. Weitere Spalten: Leistung, Datum, Mitarbeiter, Bemerkung, alles optional, wenn unten eine
            Vorgabe gesetzt ist.
          </p>
          <pre className="small muted" style={{ background: 'var(--canvas)', padding: 12, borderRadius: 8, overflowX: 'auto' }}>
            {`Youtilly-ID;Straße;PLZ;Wohnung;Leistung;Datum;Bemerkung
LEG-4711-101-2611;Aderweg 1;51371;1.01;Legionellenprüfung;03.11.2026;
LEG-4711-102-2611;Aderweg 1;51371;1.02;Legionellenprüfung;03.11.2026;Bewohner schwerhörig`}
          </pre>
          <div className="row">
            <Field label="Leistung, wenn die Datei keine nennt">
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Aus der Datei</option>
                {templates.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Zuständig, wenn die Datei niemanden nennt">
              <PersonPicker value={assigneeId} onChange={(id) => setAssigneeId(id)} />
            </Field>
          </div>
          <Field label="Excel- oder CSV-Datei">
            <input
              type="file"
              accept={TABLE_ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) readTableFile(file).then(setCsv, (err) => setError(err instanceof ApiError ? err.message : 'Die Datei konnte nicht gelesen werden.'))
              }}
            />
          </Field>
          <ColumnMapper csv={csv} target="tasks" onChange={setMapping} />
        </>
      )}
    </Modal>
  )
}
